import { watch, nextTick, type Ref } from 'vue';
import type { TableElement } from '../model/form.types';

interface UseTableGridSyncOptions {
  calculateActualRowHeights: () => void;
  onLayoutChange?: () => void;
}

// A compact fingerprint of the cell structure that can change a column's rendered width:
// whether each cell holds content, its content type, and its span. It intentionally ignores
// inner content edits (e.g. typing text) so the layout recompute fires on add/remove/merge,
// not on every keystroke.
function getCellsSignature(tableElement: TableElement): string {
  const cells = tableElement.properties.cells;
  if (!cells) return '';

  return cells
    .map((row) =>
      row
        .map((cell) => `${cell.content?.type ?? '_'}:${cell.colSpan}:${cell.rowSpan}`)
        .join(',')
    )
    .join('|');
}

export function useTableGridSync(
  tableElement: Ref<TableElement>,
  options: UseTableGridSyncOptions
) {
  const { calculateActualRowHeights, onLayoutChange } = options;

  watch(
    () => ({
      columns: tableElement.value.properties.columns,
      columnWidths: tableElement.value.properties.columnWidths,
      rows: tableElement.value.properties.rows,
      rowHeights: tableElement.value.properties.rowHeights,
      tableWidth: tableElement.value.properties.tableWidth,
      cellsSignature: getCellsSignature(tableElement.value),
    }),
    () => {
      nextTick(() => {
        calculateActualRowHeights();
        onLayoutChange?.();
      });
    },
    { flush: 'post', deep: true }
  );
}
